const FILTER = new WeakMap();
const API = new WeakMap();

export const service = (MODULE) => {
  Service.init.$inject = ['$filter', 'BookingAPI']
  return angular.module(MODULE)
    .factory('Reservation', Service.init);
}

export class Reservation {
  
  constructor(booking, filter) {
    this.$filter = filter;
    Object.assign(this, booking);
  }

  get rowClass() {
    return `${this.status}Booking`;
  }

  get guests() {
    return this.adults + (this.children || 0);
  }

  get displayName() {
    if (this.group) return this.group_name;
    return `${this.last_name}, ${this.first_name}`;
  }

  get displayConfirmation() {
    return `${this.company.nickname}${this.confirmation}`
  }

  get displayPickup() {
    const $filter = this.$filter;
    if (this.needs_pickup) return $filter('readTime')(this.pickup.time, 'h:mm a', true);
    return '-';
  }

  get averageRating(){
    const cards = this.payment.cards;
    let charges = [];
    let ratings = [];

    angular.forEach(this.payment, function (payGroup, key) {
      if (payGroup.constructor === Array && payGroup.length) {
        if (key === 'cards') {
          angular.forEach(payGroup, function (card, i) {
            charges = charges.concat(card.charges);
          });
        } else {
          // not capturing ratings on cash yet
          if (key !== 'cash') {
            charges = charges.concat(payGroup);
          }
        }
      }
    });

    ratings = ratings.concat(charges.map(function (charge) {
      if (charge.rating != 0) return charge.rating;
    })).filter(function (rating) {
      return rating !== undefined;
    });

    return (ratings.length > 0) ? ratings.reduce(function (prev, now) {
      return (prev || 0) + now;
    }, 0) / ratings.length : 0;

  }

  isCancelled(){
    const cancelled = this.trace.filter((val, key) => {
      return val.reason == 'cancelled'
    }).sort(function(a, b){
      return a.date < b.date;
    })
    return cancelled.length > 0 ? cancelled[0] : null
  }
  
  chargeCardOnFile() {
    return this.cart.payments.bill_to.find(function(bill){ return bill.name === 'CARD ON FILE'});
  }

  /**
   * Helper method to determine if the booking is paid up
   */
  isPaid() {
    return this.cart.balance <= 0;
  }

  /**
   * Get a list of the payment types made against a reservation
   */
  paymentTypes() {
    const types = [];
    angular.forEach(this.cart.payments, (payments, type) => {
      const valid = payments.filter(payment => Number.parseFloat(payment.total) > 0);
      if(valid.length) {
        switch(type){
          case 'bill_to':
            valid.forEach(bill => {
              if(bill.name) {
                types.push(bill.name);
              }
            });
            break;
          case 'cards':
            valid.filter(payment => !payment.ignore || payment.ignore === undefined).forEach(card => {
              types.push(`${card.name} ${card.masked}`);
            });
            break;
          default:
            types.push(type)
        }
      }
    })
    return types;
  }

  hasCharges(){
    const charges = this.cart.payments.cards.filter(card => Number.parseFloat(card.total) > 0);
    return charges.length ? charges : undefined;
  }

  /**
   * Determine if the booking is in the past (i.e. no longer active)
   */
  isPast() {
    const flightDay = moment.utc(this.day);
    const today = moment().hours(0).minutes(0).seconds(0)
    return flightDay.isSameOrBefore(today, 'days');
  }

  isVip() {
    return this.vip || this.modifiers.vip;
  }

  isHotel() {
    return this.hotel || this.modifiers.hotel;
  }

  isGroup() {
    return this.group || this.modifiers.group;
  }

  isModified() {
    const hotel = this.isHotel();
    const vip = this.isVip();
    const group = this.isGroup();
    return (hotel || vip || group);
  }

  statusIs(test) {
    return this.status === test;
  }

  statusIsNot(test) {
    return !this.statusIs(test);
  }

  instructions(){

    if(!this.pickup && !this.notes) return;

    let html = ``;

    function Header(text) {
      return `${html}<h6 style="margin-bottom: 0;">${text}</h6>`;
    }

    function Paragraph(text) {
      return `${html}<p>${text}</p>`;
    }

    if (this.notes) {
      if (this.notes.instructions) {
        html = Header('Special Instructions');
        html = Paragraph(this.notes.instructions);
      }
      if (this.notes.internal) {
        html = Header('Internal Notes');
        html = Paragraph(this.notes.internal);
      }
      if (this.notes.memo) {
        html = Header('Memo');
        html = Paragraph(this.notes.memo);
      }
      if (this.notes.memo) {
        html = Header('Occasion');
        html = Paragraph(this.notes.occasion);
      }
    }

    if (this.pickup) {
      if (this.pickup.notes) {
        html = Header('Notes for Driver');
        html = Paragraph(this.pickup.notes);
      }
    }

    return html !== `` ? `<div style="text-align: left;">${html}</div>` : undefined;
  }

}

export default class Service {
  
  /* @ngInject */
  constructor($filter, BookingAPI) {
    FILTER.set(this, $filter);
    this.api = BookingAPI;
    this.update = this.update;
  }

  fill(booking) {
    let book = new Reservation(booking, FILTER.get(this));
    Object.defineProperties(book, {
      'update': {
        enumerable: false,
        configurable: false,
        writable: false,
        value: this.update.bind(book)
      },
      'cancel': {
        enumerable: false,
        configurable: false,
        writable: false,
        value: this.cancel.bind(book)
      },
      'receipt': {
        enumerable: false,
        configurable: false,
        writable: false,
        value: this.receipt.bind(book)
      },
      'api': {
        enumerable: false,
        configurable: false,
        writable: false,
        value: this.api
      }
    });
    return book;
  }

  update(params) {
    const api = this.api.req();
    const data = Object.assign({ _id: this._id }, params);
    return api.update(data).$promise;
  }

  cancel(cancellation) {
    const api = this.api.req();
    const data = { _id: this._id, cancellation: cancellation };
    return api.cancel(data).$promise;
  }

  receipt(cb) {
    const api = this.api.req();
    const data = { _id: this._id };
    this.sending_receipt = true;
    return api.receipt(data).$promise.then(res => {
      this.sending_receipt = false;
      this.receipt_sent = true;
      setTimeout(() => {
        this.receipt_sent = false;
      }, 1000)
      return res;
    });
  }

  /* @ngInject */
  static init($filter, BookingAPI){
    return new Service($filter, BookingAPI)
  }

}



// WEBPACK FOOTER //
// ./client/assets/scripts/modules/reservations/reservationHelper.js